import {Box, getThemeValue, ThemeZIndexPath, useFoundationTheme} from 'platform/foundation';
import {cn, cva} from 'platform/shared';

import {ReactNode} from 'react';

import {isNil} from 'ramda';

import {suffixTestId, TestIdProps} from 'shared/web';

export type OverlayVariant = 'white' | 'neutral';
export type OverlayDensity = 'default' | 'high';

interface OverlayProps extends TestIdProps {
  variant: OverlayVariant;
  density?: OverlayDensity;
  zIndex?: ThemeZIndexPath;
  children: ReactNode;
}

const overlayVariants = cva('absolute inset-0 overflow-hidden');
const overlayContentVariants = cva('absolute inset-0 z-[1]');
const overlayBackgroundVariants = cva('absolute inset-0', {
  variants: {
    variant: {
      white: 'bg-[var(--color-white)]',
      neutral: 'bg-[var(--color-palettes-neutral-900-100)]',
    },
    density: {
      default: 'opacity-20',
      high: 'opacity-60',
    },
  },
});

export function Overlay(props: OverlayProps) {
  const theme = useFoundationTheme();
  const zIndexValue = isNil(props.zIndex)
    ? theme.zIndices.OVERLAY
    : getThemeValue<string | number>(theme.zIndices, props.zIndex);

  return (
    <div
      data-testid={suffixTestId('overlay', props)}
      className={cn(overlayVariants())}
      style={{zIndex: zIndexValue}}
    >
      <Box width="100%" height="100%" position="relative" overflow="hidden">
        <div
          className={cn(overlayContentVariants())}
          data-testid={suffixTestId('overlayContent', props)}
        >
          {props.children}
        </div>
        <div
          className={cn(
            overlayBackgroundVariants({
              variant: props.variant,
              density: props.density ?? 'default',
            })
          )}
          data-testid={suffixTestId('overlayBackground', props)}
        />
      </Box>
    </div>
  );
}
